// components/MunicipioSelect.tsx

"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  MapPin,
  Search,
  X,
} from "lucide-react";

import colombia from "@/data/colombia.json";

interface Answer {
  textValue?: string;

  question: {
    question: string;
    description?: string;
    isRequired?: boolean;
  };
}

type DepartamentoJSON = {
  id: number;
  departamento: string;
  ciudades: string[];
};

type Municipio = {
  departamento: string;
  municipio: string;
};

interface MunicipioSelectProps {
  answer: Answer;

  onBlur: (value: {
    textValue: string;
  }) => void;

  disabled?: boolean;
}

export function MunicipioSelect({
  answer,
  onBlur,
  disabled,
}: MunicipioSelectProps) {
  const [search, setSearch] = useState(
    answer.textValue || ""
  );

  const [isOpen, setIsOpen] = useState(false);

  const [isFocused, setIsFocused] =
    useState(false);

  const [isSaved, setIsSaved] = useState(false);

  const [selected, setSelected] =
    useState<Municipio | null>(null);

  const containerRef =
    useRef<HTMLDivElement>(null);

  /**
   * Limpia caracteres unicode
   */
  const cleanText = (text: string) => {
    try {
      return decodeURIComponent(escape(text))
        .normalize("NFC")
        .trim();
    } catch {
      return text.normalize("NFC").trim();
    }
  };

  /**
   * Convierte departamentos -> municipios
   */
  const municipios: Municipio[] = useMemo(() => {
    return (colombia as DepartamentoJSON[]).flatMap(
      (dep) =>
        dep.ciudades.map((ciudad) => ({
          departamento: cleanText(
            dep.departamento
          ),
          municipio: cleanText(ciudad),
        }))
    );
  }, []);

  /**
   * Filtrar municipios
   */
  const filteredMunicipios = useMemo(() => {
    return municipios.filter((item) =>
      `${item.departamento} ${item.municipio}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [search, municipios]);

  /**
   * Click afuera
   */
  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent
    ) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(
          event.target as Node
        )
      ) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  /**
   * Sincronizar answer
   */
  useEffect(() => {
    setSearch(answer.textValue || "");
  }, [answer.textValue]);

  /**
   * Seleccionar municipio
   */
  const handleSelect = async (
    item: Municipio
  ) => {
    const value = `${item.departamento} - ${item.municipio}`;

    setSelected(item);

    setSearch(value);

    setIsOpen(false);

    try {
      await onBlur({
        textValue: value,
      });

      setIsSaved(true);

      setTimeout(() => {
        setIsSaved(false);
      }, 2000);
    } catch (error) {
      console.error(
        "Error saving municipio:",
        error
      );
    }
  };

  const handleClear = async () => {
    setSearch("");
    setSelected(null);
    setIsOpen(false);

    try {
      await onBlur({
        textValue: "",
      });
    } catch (error) {
      console.error(
        "Error clearing municipio:",
        error
      );
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        position: "relative",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 8,
        }}
      >
        <div>
          <label
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#111827",
              display: "block",
              marginBottom: 4,
            }}
          >
            {answer.question.question}

            {answer.question.isRequired && (
              <span
                style={{
                  color: "#ef4444",
                  marginLeft: 4,
                }}
              >
                *
              </span>
            )}
          </label>

          {answer.question.description && (
            <p
              style={{
                fontSize: 14,
                color: "#6b7280",
              }}
            >
              {answer.question.description}
            </p>
          )}
        </div>

        <AnimatePresence>
          {isSaved && (
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.8,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.8,
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: "#10b981",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Check size={16} />
              <span>Guardado</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* INPUT */}
      <div
        style={{
          position: "relative",
        }}
      >
        <Search
          size={18}
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#9ca3af",
          }}
        />

        <input
          type="text"
          value={search}
          disabled={disabled}
          placeholder="Buscar municipio o departamento..."
          onFocus={() => {
            setIsFocused(true);
            setIsOpen(true);
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          style={{
            width: "100%",
            padding: "14px 44px",
            borderRadius: 16,
            border: `2px solid ${isFocused
                ? "#3b82f6"
                : "#e5e7eb"
              }`,
            outline: "none",
            fontSize: 15,
            transition: "all 0.2s ease",
            backgroundColor: isFocused
              ? "#ffffff"
              : "#f9fafb",
            color: "#111827",
          }}
        />

        {search ? (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9ca3af",
              transition: "0.2s",
            }}
          >
            <X size={18} />
          </button>
        ) : (
          <ChevronDown
            size={18}
            style={{
              position: "absolute",
              right: 14,
              top: "50%",
              transform: `translateY(-50%) rotate(${isOpen ? "180deg" : "0deg"
                })`,
              transition: "0.2s",
              color: "#9ca3af",
            }}
          />
        )}
      </div>

      {/* DROPDOWN */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              y: -10,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: -10,
            }}
            transition={{
              duration: 0.15,
            }}
            style={{
              position: "absolute",
              zIndex: 50,
              width: "100%",
              marginTop: 8,
              background: "white",
              borderRadius: 20,
              border:
                "1px solid #e5e7eb",
              maxHeight: 320,
              overflowY: "auto",
              boxShadow:
                "0 10px 30px rgba(0,0,0,0.08)",
            }}
          >
            {filteredMunicipios.length ===
              0 && (
                <div
                  style={{
                    padding: 20,
                    textAlign: "center",
                    color: "#6b7280",
                    fontSize: 14,
                  }}
                >
                  No se encontraron municipios
                </div>
              )}

            {filteredMunicipios.map(
              (item, index) => {
                const isSelected =
                  selected?.departamento ===
                  item.departamento &&
                  selected?.municipio ===
                  item.municipio;

                return (
                  <motion.button
                    key={`${item.departamento}-${item.municipio}-${index}`}
                    whileHover={{
                      backgroundColor:
                        "#f3f4f6",
                    }}
                    type="button"
                    onClick={() =>
                      handleSelect(item)
                    }
                    style={{
                      width: "100%",
                      border: "none",
                      background:
                        isSelected
                          ? "#eff6ff"
                          : "transparent",
                      padding:
                        "14px 18px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "space-between",
                      transition: "0.2s",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems:
                          "center",
                        gap: 12,
                        textAlign:
                          "left",
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 12,
                          background:
                            "#eff6ff",
                          display: "flex",
                          alignItems:
                            "center",
                          justifyContent:
                            "center",
                          color:
                            "#2563eb",
                        }}
                      >
                        <MapPin
                          size={16}
                        />
                      </div>

                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            color:
                              "#111827",
                            fontSize: 14,
                          }}
                        >
                          {
                            item.municipio
                          }
                        </div>

                        <div
                          style={{
                            fontSize: 13,
                            color:
                              "#6b7280",
                          }}
                        >
                          {
                            item.departamento
                          }
                        </div>
                      </div>
                    </div>

                    {isSelected && (
                      <Check
                        size={18}
                        color="#2563eb"
                      />
                    )}
                  </motion.button>
                );
              }
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* FOOTER */}
      <div
        style={{
          marginTop: 8,
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          fontSize: 12,
          color: "#6b7280",
        }}
      >
        <span>
          {filteredMunicipios.length} municipios
        </span>

        {selected && (
          <span
            style={{
              color: "#2563eb",
              fontWeight: 500,
            }}
          >
            Seleccionado
          </span>
        )}
      </div>
    </div>
  );
}